import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { PlatformReleaseInfo } from "./components/PlatformReleaseInfo";
import AudioService from "../../core/services/audioService";
import isEmpty from "lodash/isEmpty"
import { useParams } from "react-router";
import { ProgressSpinner } from "primereact/progressspinner";
import { useRelease } from "components/NewRelease/hooks/useRelease";
import { useLocation } from "react-router-dom";


export const FormContent: React.FC = () => {
  const { id } = useParams()
  const { getAudioData } = AudioService()
  const [audioMap, setAudioMap] = useState<Record<string, string>>({});
  const audioUrlsRef = useRef<Record<string, string>>({});
  const isUnmountedRef = useRef(false);
  const { release, fetching, fetchRelease } = useRelease(id)
  const location = useLocation();

  const trackIds = useMemo(() => {
    const tracks = (release as any)?.tracks || [];
    return Array.isArray(tracks) ? tracks.map((t: any) => String(t?.id)).filter(Boolean) : [];
  }, [(release as any)?.tracks]);

  const audio = useMemo(() => {
    return Object.entries(audioMap).map(([tid, url]) => ({ id: tid, audio: url }));
  }, [audioMap]);

  // Lazy audio load: render track info immediately, then fetch audio per-track in background.
  useEffect(() => {
    if (!trackIds.length) return;

    // Revoke URLs for tracks that are no longer present
    const currentSet = new Set(trackIds);
    Object.keys(audioUrlsRef.current).forEach((tid) => {
      if (!currentSet.has(tid)) {
        try { URL.revokeObjectURL(audioUrlsRef.current[tid]); } catch { /* noop */ }
        delete audioUrlsRef.current[tid];
      }
    });

    let cancelled = false;
    const CONCURRENCY = 2;
    const queue = trackIds.filter((tid) => !audioUrlsRef.current[tid]);

    const fetchOne = async (tid: string) => {
      try {
        const res = await getAudioData(`tracks/${tid}/stream`);
            const audioBlob = new Blob([res.data], { type: "audio/wav" });
        const url = URL.createObjectURL(audioBlob);
        if (cancelled || isUnmountedRef.current) {
          try { URL.revokeObjectURL(url); } catch { /* noop */ }
          return;
        }
        audioUrlsRef.current[tid] = url;
        setAudioMap((prev) => ({ ...prev, [tid]: url }));
          } catch (error) {
        console.error(`Failed to load audio for track ${tid}`, error);
          }
    };

    const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
      while (queue.length && !cancelled && !isUnmountedRef.current) {
        const tid = queue.shift();
        if (!tid) break;
        await fetchOne(tid);
      }
    });

    Promise.all(workers).catch(() => { /* ignore */ });

    return () => {
      cancelled = true;
    };
  }, [trackIds, getAudioData]);

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
      Object.values(audioUrlsRef.current).forEach((url) => {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
      });
      audioUrlsRef.current = {};
    };
  }, []);

  useEffect(() => {
    if(location.pathname === `/releases/${id}`){
      fetchRelease();      
    }
  }, []);


  return (
    <Container>
      {fetching ? (
        <Loading>
          <ProgressSpinner />
        </Loading>
      ) :
      <PlatformReleaseInfo
        releases={release}
        audio={audio}
        loading={false}
      />}

    </Container>
  );
};

const Container = styled.div`
    background-color: ${(props) => props.theme.palette.background.base};
    width: 100%;
    min-height: 80vh;
`;

const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;